import Box from '@components/base/Box'
import {
  AvailableTicketInfo,
  EventTicket,
  TicketType,
} from '@graphql/__generated/graphql'
import currencies from '@libs/currencies'
import { useEffect, useRef, useState } from 'react'

type TicketDropdownInputProps = {
  label?: string
  tickets: AvailableTicketInfo[]
  ticketDetails?: EventTicket[]
  currency?: string
  value?: string
  show?: boolean
  required?: boolean
  onChange?: (value: string) => void
}

const getTicketPrice = (
  ticketTitle: string | null | undefined,
  ticketDetails?: EventTicket[]
) => {
  return ticketDetails?.find(
    (ticket) => ticket.title.toUpperCase() === ticketTitle?.toUpperCase()
  )
}

const ticketHasDiscounts = (ticket: AvailableTicketInfo) => {
  return !!ticket?.discountCodes && ticket?.discountCodes?.length > 0
}

const getDiscountsRemaining = (ticket: AvailableTicketInfo) => {
  return (
    ticket.discountCodes?.reduce((total, discount) => {
      return total + (discount?.remaining ?? 0)
    }, 0) ?? 0
  )
}

const isNoCheckoutTicket = (ticket?: EventTicket) =>
  ticket?.ticketType === TicketType.Free ||
  ticket?.ticketType === TicketType.InvoiceTicket ||
  ticket?.price === 0

const SHOW_DISCOUNTS = false
const REMAINING_TICKET_THRESHOLD = 20

export const TicketDropdownInput = ({
  label = 'Ticket Type',
  tickets,
  ticketDetails,
  currency = 'GBP',
  value,
  show = true,
  required = false,
  onChange,
}: TicketDropdownInputProps) => {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currencyDetails = currencies[currency.toUpperCase()] ?? currencies.GBP
  const selectedTicket = value
    ? tickets.find(
        (ticket) => ticket.ticketTitle?.toUpperCase() === value?.toUpperCase()
      )
    : undefined

  const dropdownTickets =
    selectedTicket && tickets.length > 1
      ? tickets.filter(
          (ticket) =>
            ticket.ticketTitle?.toUpperCase() !==
            selectedTicket.ticketTitle?.toUpperCase()
        )
      : tickets

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  if (!show) return null

  const formatTicket = (ticket: AvailableTicketInfo) => {
    const title = ticket.ticketTitle ?? ''
    const details = getTicketPrice(title, ticketDetails)
    const isWaitingList = details?.ticketType === TicketType.WaitingList
    const remainingCount = parseInt(ticket.remaining ?? '0', 10)
    const isSoldOut = !isWaitingList && remainingCount < 1
    const hasDiscounts = ticketHasDiscounts(ticket)
    const discountsRemaining = getDiscountsRemaining(ticket)
    const formattedPrice = `${currencyDetails.symbol}${(details?.price ?? 0).toFixed(2)}`
    const priceLabel =
      details?.ticketType === TicketType.InvoiceTicket
        ? `${formattedPrice} invoiced later`
        : isNoCheckoutTicket(details)
          ? 'Free'
          : formattedPrice

    return {
      hasDiscounts,
      discountsRemaining,
      isSoldOut,
      isWaitingList,
      priceLabel,
      remainingLabel: isWaitingList
        ? 'Waiting list'
        : isSoldOut
          ? 'Sold out'
        : remainingCount > REMAINING_TICKET_THRESHOLD
          ? `Available`
          : `${remainingCount} Left`,
      title,
    }
  }

  return (
    <Box
      className="relative z-50 mb-4 flex flex-col space-y-2"
      innerRef={dropdownRef}
    >
      {label && (
        <Box className="text-sm font-medium">
          {label}
          {required ? <span className="ml-1 text-red-500">*</span> : null}
        </Box>
      )}
      <button
        type="button"
        onClick={() => setOpen((state) => !state)}
        className="w-full rounded border border-gray-300 bg-white p-3 text-left text-gray-900 transition-all hover:border-forumm-blue"
      >
        {selectedTicket ? (
          <TicketDropdownItem {...formatTicket(selectedTicket)} />
        ) : (
          <Box className="flex items-center justify-between gap-3">
            <Box>
              <Box className="font-semibold leading-tight">Select a ticket</Box>
              <Box className="mt-1 text-sm opacity-75">
                Choose from available ticket options
              </Box>
            </Box>
            <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-700">
              Required
            </span>
          </Box>
        )}
      </button>
      <Box
        className={`${
          open ? 'absolute top-full z-50 mt-1' : 'hidden'
        } flex max-h-80 w-full flex-col gap-2 overflow-y-auto rounded border border-gray-300 bg-white p-2 text-gray-900 shadow-xl`}
      >
        {dropdownTickets.map((ticket) => {
          const ticketDisplay = formatTicket(ticket)
          const { isSoldOut, title } = ticketDisplay
          const selected =
            title.toUpperCase() === selectedTicket?.ticketTitle?.toUpperCase()

          return (
            <button
              key={title}
              type="button"
              disabled={isSoldOut}
              onClick={() => {
                onChange?.(title.toUpperCase())
                setOpen(false)
              }}
              className={`w-full rounded border p-3 text-left transition-all ${
                selected
                  ? 'border-forumm-blue bg-forumm-blue/10'
                  : 'border-gray-200 bg-white hover:border-forumm-blue'
              } ${
                isSoldOut
                  ? 'cursor-not-allowed border-red-100 bg-red-50'
                  : ticketDisplay.isWaitingList
                    ? 'cursor-pointer border-amber-200 bg-amber-50'
                    : 'cursor-pointer'
              }`}
            >
              <TicketDropdownItem {...ticketDisplay} />
            </button>
          )
        })}
      </Box>
    </Box>
  )
}

type TicketDropdownItemProps = {
  hasDiscounts: boolean
  discountsRemaining: number
  isSoldOut: boolean
  isWaitingList: boolean
  priceLabel: string
  remainingLabel: string
  title: string
}

const TicketDropdownItem = ({
  hasDiscounts,
  discountsRemaining,
  isSoldOut,
  isWaitingList,
  priceLabel,
  remainingLabel,
  title,
}: TicketDropdownItemProps) => (
  <Box className="flex items-start justify-between gap-3">
    <Box className="min-w-0">
      <Box
        className={`font-semibold leading-tight ${
          isSoldOut ? 'text-gray-500 line-through' : ''
        } ${isWaitingList ? 'text-amber-900' : ''}`}
      >
        {title}
      </Box>
      <Box className="mt-1 text-sm opacity-75">{priceLabel}</Box>
    </Box>
    <Box className="flex shrink-0 flex-wrap justify-end gap-1">
      <span
        className={`rounded-full px-2 py-1 text-[11px] font-medium ${
          isSoldOut
            ? 'bg-red-100 text-red-700'
            : isWaitingList
              ? 'bg-amber-100 text-amber-800'
              : 'bg-gray-100 text-gray-700'
        }`}
      >
        {remainingLabel}
      </span>
      {!isWaitingList && hasDiscounts && SHOW_DISCOUNTS && (
        <span
          className={`rounded-full px-2 py-1 text-[11px] font-medium ${
            discountsRemaining > 0
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {discountsRemaining > 0 ? `Discounts Available` : 'No discounts'}
        </span>
      )}
    </Box>
  </Box>
)
